// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import IconClose from "@conklin/XDL/Icons/IconClose";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, layoutContainer, xScreen } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    message = "message",
    bgColor = "bgColor",
    textColor = "textColor",
    showClose = "showClose",
}

interface Props extends WidgetProps {
    fields: {
        [fields.message]: string;
        [fields.bgColor]: string;
        [fields.textColor]: string;
        [fields.showClose]: boolean;
    };
}

export interface GlobalNotificationBarStyles {
    wrapper?: InjectableCss;
    container?: InjectableCss;
    message?: TypographyProps;
    closeIcon?: IconProps;
}

export const globalNotificationBarStyles: GlobalNotificationBarStyles = {
    wrapper: {
        css: css`
            ${({ bgColor }: any) => {
                return css`
                    background-color: ${bgColor};
                `;
            }}
            padding-top: 5px;
            padding-bottom: 5px;
        `,
    },
    container: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            ${layoutContainer};

            && {
                ${xScreen("lte", "md")} {
                    padding-left: 24px;
                    padding-right: 24px;
                }
            }
        `,
    },
    message: {
        size: 14,
        lineHeight: "22px",
        weight: fontConfig.weight.regular,
        css: css`
            text-align: center;
        `,
    },
    closeIcon: {
        size: 12,
        color: "common.ui1",
        css: css`
            position: absolute;
            right: 16px;
            top: 50%;
            transform: translateY(-50%);
            cursor: pointer;

            &:hover {
                color: ${color("common.ui9")};
            }

            ${xScreen("lte", "sm")} {
                top: 7px;
                right: 8px;
                transform: none;
            }
        `,
    },
};

const styles = globalNotificationBarStyles;

const GlobalNotificationBar: React.FunctionComponent<Props> = (props: Props) => {
    const [visible, setVisibility] = React.useState(true);

    if (!visible) {
        return null;
    }
    const {
        fields: { message, bgColor, textColor, showClose },
    } = props;

    return (
        <StyledWrapper {...styles.wrapper} {...{ bgColor }}>
            <StyledWrapper {...styles.container}>
                {message && (
                    <Typography {...styles.message} color={textColor}>
                        {message}
                    </Typography>
                )}
                {showClose && <Icon src={IconClose} {...styles.closeIcon} onClick={() => setVisibility(false)} />}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: GlobalNotificationBar,
    definition: {
        group: "Basic",
        icon: "Info",
        fieldDefinitions: [
            {
                name: fields.message,
                displayName: "Message",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                sortOrder: 1,
            },
            {
                name: fields.bgColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#d03f33",
                fieldType: "General",
                isRequired: true,
                sortOrder: 2,
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#ffffff",
                fieldType: "General",
                isRequired: true,
                sortOrder: 3,
            },
            {
                name: fields.showClose,
                displayName: "Show close icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
        allowedContexts: ["Header", "CompactHeader"],
    },
};

export default widgetModule;
